import React, {useEffect, useRef, useState} from "react";
import {Box, Text, useLocalStorage} from "@unibuddy/patron";
import styled, {css} from "styled-components";

import {IconArrowRight, IconChevronDown} from "../../icons";
import {ASSISTANT_SHOULD_SHOW_ACTIONS_KEY} from "../CTAActions/AppCTAActions";
import {UnibuddyLogo} from "../../components/UnibuddyLogo/UnibuddyLogo";
import {usePostMessage} from "../../hooks/usePostMessage";

const AnimationContainer = styled.button<{appVisible?: boolean}>`
    all: unset;
    cursor: pointer;
    padding: 8px;
    width: 32px;
    height: 32px;
    border-radius: 40px;
    float: right;
    position: relative;
    color: white;
    background-color: ${({theme}) => theme.colors.primaryColor};
    transition: ${(props) =>
        !props.appVisible ? "all 0.3s ease" : "width 0.3s ease, background-color 0.1s ease"};
    -webkit-transition: ${(props) =>
        !props.appVisible ? "all 0.3s ease" : "width 0.3s ease, background-color 0.1s ease"};
    overflow: hidden;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    gap: 12px;

    :focus {
        outline: ${({theme}) => `2px solid ${theme.colors.blue400}`};
    }

    ${({theme, appVisible}) =>
        appVisible
            ? css`
                  &:hover {
                      background-color: ${theme.colors.primaryHoverColor};
                  }
              `
            : css`
                  &:hover {
                      width: 150px;
                      gap: 8px;
                      background-color: ${theme.colors.primaryHoverColor};
                  }
              `};
`;

export function AppCTAButton() {
    const urlParams = new URLSearchParams(window.location.search);
    const universityId = urlParams.get("universityId") ?? "";
    const {postMessage} = usePostMessage();
    const ctaButtonRef = useRef<HTMLButtonElement | null>(null);
    const [appVisible, setAppVisible] = useState<boolean>(false);
    const [_, setShouldShowActions] = useLocalStorage(
        `${ASSISTANT_SHOULD_SHOW_ACTIONS_KEY}-${universityId}`,
    ) as [boolean, (b: boolean) => void];

    useEffect(() => {
        const messageListener = (e: MessageEvent) => {
            switch (e.data) {
                case "chatVisible=true":
                    setAppVisible(true);
                    break;
                case "chatVisible=false":
                    setAppVisible(false);
                    ctaButtonRef.current?.focus();
                    break;
                default:
                    break;
            }
        };

        window.addEventListener("message", messageListener, true);

        return () => {
            window.removeEventListener("message", messageListener);
        };
    }, []);

    return (
        <AnimationContainer
            title={`${appVisible ? "Close" : "Open"} Assistant`}
            aria-label={`${appVisible ? "Close" : "Open"} Assistant`}
            aria-expanded={appVisible}
            aria-haspopup="dialog"
            appVisible={appVisible}
            onClick={() => {
                postMessage(`chatVisible=${!appVisible as unknown as string}`);
                postMessage("showCTAActions=false");
                setShouldShowActions(false);
                setAppVisible(!appVisible);
            }}
            ref={ctaButtonRef}
        >
            {appVisible ? (
                <IconChevronDown color="white" width="32px" height="32px" />
            ) : (
                <>
                    <Box
                        display="flex"
                        flexShrink={0}
                        height="32px"
                        width="32px"
                        justifyContent="center"
                        paddingTop="xxsmall"
                    >
                        <UnibuddyLogo color="white" width="30px" height="28px" />
                    </Box>
                    <Box display="flex" flexShrink={0}>
                        <Text color="white" nowrap weight="bold">
                            Open chat
                        </Text>
                    </Box>
                    <Box display="flex" flexShrink={0}>
                        <IconArrowRight color="white" width="24px" height="24px" />
                    </Box>
                </>
            )}
        </AnimationContainer>
    );
}
